import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../api/client'
import { useDeleteCustomer } from '../api/queries'
import type { Customer, CustomerDeleteSummary } from '../api/types'
import { Card } from './ui'

function count(value: number, one: string, many: string) {
  return value === 1 ? `1 ${one}` : `${value} ${many}`
}

/**
 * Slet kunden helt - kun for Superadmin (beslutning 079).
 *
 * Tænkt til testkunder og kunder oprettet ved en fejl. Før sletningen hentes, hvad der
 * står i vejen, og hvad der følger med. Står noget i vejen, siges det med serverens
 * egne ord - "Kunden har 3 sager" - i stedet for at spørge, om man er sikker, og så fejle.
 */
export function DeleteCustomerCard({
  customer,
  onDeleted
}: {
  customer: Customer
  /** Kaldes efter sletningen - f.eks. så redigeringsformularen lukker sig selv. */
  onDeleted?: () => void
}) {
  const remove = useDeleteCustomer()
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function run() {
    setError(null)
    setBusy(true)
    try {
      const summary = await api.get<CustomerDeleteSummary>(`/api/customers/${customer.id}/delete-summary`)

      if (summary.blockedReason) {
        setError(summary.blockedReason)
        return
      }

      const gone = [
        summary.warranties && count(summary.warranties, 'garanti', 'garantier'),
        summary.accessKeys && count(summary.accessKeys, 'nøgle', 'nøgler')
      ].filter(Boolean)

      const kept = [
        summary.supportTickets && count(summary.supportTickets, 'henvendelse', 'henvendelser'),
        summary.fieldMessages && count(summary.fieldMessages, 'besked til marken', 'beskeder til marken')
      ].filter(Boolean)

      const message =
        `Slet kunden "${customer.name}" permanent?\n\n` +
        (gone.length ? `Slettes med kunden: ${gone.join(', ')}.\n` : 'Der er intet andet på kunden.\n') +
        (kept.length ? `Bliver, men mister koblingen til kunden: ${kept.join(', ')}.\n` : '') +
        '\nDet kan ikke fortrydes. Er det en rigtig kunde, så deaktivér den i stedet.'

      if (!window.confirm(message)) return

      await remove.mutateAsync(customer.id)
      onDeleted?.()
      navigate('/customers')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kunden kunne ikke slettes.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title="Slet kunden">
      <p className="small muted" style={{ marginTop: 0 }}>
        Kun for Superadmin - til testkunder og kunder oprettet ved en fejl. Kunden skal først være uden sager,
        enheder og abonnementer. En rigtig kunde skal deaktiveres, så historikken bliver.
      </p>
      {error && <div className="error-box" style={{ marginBottom: 10 }}>{error}</div>}
      <button className="btn btn--danger btn--sm" disabled={busy} onClick={() => void run()}>
        {busy ? 'Sletter ...' : 'Slet kunden permanent'}
      </button>
    </Card>
  )
}
